import {
  Box,
  Chip,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import type { PaymentPoDetails, PaymentScheduleForm, PoModalModifiers, Vendor } from 'api/types';
import AddCirlce from 'assets/images/add_circle.svg';
import CheckCirleSuccess from 'assets/images/check_circle_success.svg';
import DeleteIcon from 'assets/images/delete-icon.svg';
import CustomButton from 'components/NewLayout/Button';
import CustomDialog from 'components/NewLayout/Dialog';
import DropDown from 'components/NewLayout/Dropdown';
import CustomTextfield from 'components/NewLayout/Textfield';
import { useMemo, useReducer, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Validator from 'simple-react-validator';
import { paymentMethods } from 'utils/constants';
import { getValidations, MAX_11_DIGITS_TWO_DECIMALS_REGEX } from 'utils/index';

export const StyledLegend = styled('div')(({ theme }) => ({
  fontSize: '16px',
  marginBottom: theme.spacing(1),
  width: 'max-content',
  background: '#fff',
  marginTop: '-29px !important',
  paddingLeft: '7px',
  paddingRight: '5px'
}));

export default function PaymentScheduleModal({
  poModifiers,
  vendorBillingData,
  purchaseOrderId,
  selectedPoTotalPrice,
  selectedVendorPayment,
  paymentPoDetails,
  onClose,
  onComplete,
  createPurchaseOrderAndPaymentSchedule,
  onConfirmWarning,
  setPoModalData,
  onBackToCreatePo,
  purchaseDate
}: {
  poModifiers?: PoModalModifiers;
  vendorBillingData?: Vendor[];
  purchaseOrderId?: string;
  selectedPoTotalPrice: number;
  selectedVendorPayment?: string;
  paymentPoDetails?: PaymentPoDetails;
  onClose: () => void;
  onComplete?: () => void;
  createPurchaseOrderAndPaymentSchedule?: (data: PaymentScheduleForm[]) => Promise<void>;
  onConfirmWarning?: (val: boolean) => Promise<void>;
  setPoModalData?: React.Dispatch<React.SetStateAction<PoModalModifiers>>;
  onBackToCreatePo?: () => void;
  purchaseDate?: string;
}) {
  const initialPaymentScheduleObj = {
    index: 0,
    po_in_total: '',
    amount: undefined,
    due_date: purchaseDate || '',
    payment_method: selectedVendorPayment
  };
  const [modalShow, setShowModal] = useState<boolean>(true);
  const [loader, setLoader] = useState<boolean>(false);
  const [data, setData] = useState<PaymentScheduleForm[]>([initialPaymentScheduleObj]);
  const [delayPaymentScheduleModal, setDelayPaymentScheduleModal] = useState<boolean>(false);
  const validator = useRef(new Validator(getValidations()));
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const theme = useTheme();

  // let total = 0;
  // POModalData?.forEach((d) => {
  //   const price = safeParseFloat(d?.price);
  //   const quantity = safeParseFloat(d?.quantity);
  //   total += price * quantity;
  // });

  const { poModifiersTotal, totalIncludingPOModifiersTotal } = useMemo(() => {
    const modifiersTotal =
      Number(poModifiers?.estimated_taxes) +
      Number(poModifiers?.shipping_cost) -
      Number(poModifiers?.discount) -
      Number(poModifiers?.deposit) -
      Number(poModifiers?.refund);

    return {
      poModifiersTotal: modifiersTotal,
      totalIncludingPOModifiersTotal: (modifiersTotal + selectedPoTotalPrice).toFixed(2)
    };
  }, [
    poModifiers?.estimated_taxes,
    poModifiers?.shipping_cost,
    poModifiers?.discount,
    poModifiers?.deposit,
    poModifiers?.refund,
    selectedPoTotalPrice
  ]);

  const distributeAmountsByPercentages = (percentages: number[], totalAmount: number): number[] => {
    // Convert to cents to avoid floating point errors
    const totalCents = Math.round(totalAmount * 100);

    // Calculate each amount in cents (flooring to avoid over-allocation)
    const amountsInCents = percentages.map((percent) => Math.floor((totalCents * percent) / 100));

    // Calculate the sum of floored amounts
    const sumOfFloored = amountsInCents.reduce((sum, amount) => sum + amount, 0);

    // Calculate the remainder (lost pennies)
    const remainder = totalCents - sumOfFloored;

    // Distribute the remainder to ensure exact total
    const distributedAmounts = [...amountsInCents];

    // Add 1 cent to the first 'remainder' items
    for (let i = 0; i < remainder; i++) {
      distributedAmounts[i] += 1;
    }

    // Convert back to dollars
    return distributedAmounts.map((cents) => parseFloat((cents / 100).toFixed(2)));
  };

  const handleChangePOModifiers = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    if (MAX_11_DIGITS_TWO_DECIMALS_REGEX.test(value) && setPoModalData) {
      setPoModalData((prevState) => {
        if (prevState[name] === value) return prevState; // Prevent redundant updates
        return { ...prevState, [name]: value };
      });
    }
  };

  // Modify handleChangePaymentSchedule to remove the validation
  const handleChangePaymentSchedule = (event, index, field) => {
    const { name, value } = event.target;

    const getTotalPrice = () => totalIncludingPOModifiersTotal;

    if (field === 'po_in_total') {
      const percentageValue = parseFloat(value);
      if (percentageValue < 0 || percentageValue > 100) {
        toast.error('Percentage must be between 0 and 100', {
          position: 'top-center',
          id: 'invalid_percentage'
        });
        return;
      }

      setData((prevData) => {
        const updatedData = [...prevData];
        const totalPrice = Number(getTotalPrice());

        // Only update the percentage that was changed
        updatedData[index] = {
          ...updatedData[index],
          po_in_total: value
        };

        // Collect all percentages (including the updated one)
        const allPercentages = updatedData.map((item) => Number(item.po_in_total || 0));

        // Calculate properly distributed amounts for ALL items
        const distributedAmounts = distributeAmountsByPercentages(allPercentages, totalPrice);

        // Update all amounts with properly distributed values
        updatedData.forEach((item, i) => {
          updatedData[i] = {
            ...item,
            amount: distributedAmounts[i]
          };
        });

        return updatedData;
      });
      return;
    }

    if (field === 'amount') {
      setData((prevData) => {
        const updatedData = [...prevData];
        const totalPrice = getTotalPrice();

        // Update the changed amount
        updatedData[index] = {
          ...updatedData[index],
          amount: value
        };

        // Calculate total of all amounts
        const totalAmount = updatedData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

        // Ensure totalPrice is a number before arithmetic
        const numericTotalPrice = Number(totalPrice);

        // If amounts don't match total, recalculate percentages
        if (Math.abs(totalAmount - numericTotalPrice) > 0.01) {
          updatedData.forEach((item, i) => {
            const amount = Number(item.amount) || 0;
            const percentage = numericTotalPrice > 0 ? (amount / numericTotalPrice) * 100 : 0;
            updatedData[i] = {
              ...item,
              po_in_total: percentage.toFixed(2)
            };
          });
        }

        return updatedData;
      });
      return;
    }

    setData((prevData) => {
      const updatedData = [...prevData];
      updatedData[index] = {
        ...updatedData[index],
        [field]: value
      };
      return updatedData;
    });
  };

  // Update submitForm to include validation for minimum amount
  const submitForm = async () => {
    if (!validator.current.allValid()) {
      validator.current.showMessages();
      forceUpdate();
      return;
    }

    const totalPercentage = data.reduce((sum, item) => sum + Number(item.po_in_total || 0), 0);

    if (Math.abs(totalPercentage - 100) > 0.01) {
      toast.error('Percentages must sum to 100%');
      setLoader(false);
      return;
    }
    // Check if any amount is less than 0.01
    const invalidAmounts = data.filter(
      (item) =>
        item.amount !== undefined && (Number(item.amount) < 0.01 || isNaN(Number(item.amount)))
    );

    if (invalidAmounts.length > 0) {
      toast.error('Payment amounts cannot be less than 0.01', {
        position: 'top-center',
        id: 'less_than_minimum'
      });
      return;
    }

    setLoader(true);

    if (Number(totalIncludingPOModifiersTotal) < 0.01) {
      toast.error('Payment amounts cannot be less than $0.01', {
        position: 'top-center',
        id: 'less_than_minimum_2'
      });
      setLoader(false);
      return;
    }

    const newData = data.map((item: PaymentScheduleForm) => ({
      ...item,
      purchase_order_id: purchaseOrderId,
      status: 'Paid'
    }));

    const totalAmount = newData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const formattedTotalAmount = Number(totalAmount.toFixed(2));
    const maxAllowedAmount = Number(Number(totalIncludingPOModifiersTotal).toFixed(2));

    if (formattedTotalAmount !== maxAllowedAmount) {
      toast.error(`Total of Bill(s) must match $${maxAllowedAmount.toFixed(2)}`);
      setLoader(false);
      return;
    }

    try {
      await createPurchaseOrderAndPaymentSchedule?.(newData);
      setShowModal(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoader(false);
    }
  };

  const handleAddPaymentSchedule = () => {
    const newIndex = data?.length;
    const newPaymentObj = { ...initialPaymentScheduleObj, index: newIndex, due_date: '' };
    setData([...data, newPaymentObj]);
  };

  const handleDeletePaymentSchedule = (indexToDelete: number) => {
    setData((prevData) => prevData?.filter((item) => item.index !== indexToDelete));
  };

  const handleDelayPaymentScheduleCreation = () => {
    if (onConfirmWarning) {
      setLoader(true);
      onConfirmWarning(true)
        .then(() => {
          setLoader(false);
          setShowModal(false);
          setDelayPaymentScheduleModal(false);
        })
        .catch((error) => {
          console.error('Error during creation of delayed payment schedule:', error);
          setLoader(false);
        });
    }
  };

  validator.current.purgeFields();

  return (
    <>
      <CustomDialog
        title="Create Payment Schedule"
        open={modalShow}
        maxWidth="lg"
        closeDialog={() => onClose()}
        loading={loader}
        content={
          <>
            <Box className="payment-modal-table">
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Purchase Order Number</TableCell>
                      <TableCell>Shipping</TableCell>
                      <TableCell>Tax</TableCell>
                      <TableCell>Discount</TableCell>
                      <TableCell>Depost</TableCell>
                      <TableCell>Refund</TableCell>
                      <TableCell>Total PO Amount</TableCell>
                      <TableCell>Total Paid</TableCell>
                      <TableCell>Total Remaining</TableCell>
                      <TableCell>Total Scheduled</TableCell>
                      <TableCell>Vendor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow
                      key={'payment-moda-table'}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>{paymentPoDetails?.po_number || '00000'}</TableCell>
                      <TableCell>
                        <CustomTextfield
                          name="shipping_cost"
                          placeholder="$0.00"
                          onChange={handleChangePOModifiers}
                          value={poModifiers?.shipping_cost || '0.00'}
                          selectText
                        />
                      </TableCell>
                      <TableCell>
                        <CustomTextfield
                          name="estimated_taxes"
                          placeholder="$0.00"
                          onChange={handleChangePOModifiers}
                          value={poModifiers?.estimated_taxes || '0.00'}
                          selectText
                        />
                      </TableCell>
                      <TableCell>
                        <CustomTextfield
                          name="discount"
                          placeholder="$0.00"
                          onChange={handleChangePOModifiers}
                          value={poModifiers?.discount || '0.00'}
                          selectText
                        />
                      </TableCell>
                      <TableCell>
                        <CustomTextfield
                          name="deposit"
                          placeholder="$0.00"
                          onChange={handleChangePOModifiers}
                          value={poModifiers?.deposit || '0.00'}
                          selectText
                        />
                      </TableCell>
                      <TableCell>
                        <CustomTextfield
                          name="refund"
                          placeholder="$0.00"
                          onChange={handleChangePOModifiers}
                          value={poModifiers?.refund || '0.00'}
                          selectText
                        />
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {'$' +
                          (paymentPoDetails?.total_amount !== undefined
                            ? Number(paymentPoDetails.total_amount) + Number(poModifiersTotal)
                            : Number(totalIncludingPOModifiersTotal)
                          ).toFixed(2)}
                      </TableCell>
                      <TableCell>{'$' + (paymentPoDetails?.paid_amount || 0)}</TableCell>
                      <TableCell>
                        {'$' +
                          (paymentPoDetails?.remaining_amount !== undefined
                            ? Number(paymentPoDetails.remaining_amount) + poModifiersTotal
                            : Number(totalIncludingPOModifiersTotal)
                          ).toFixed(2)}
                      </TableCell>
                      <TableCell>{'$' + (paymentPoDetails?.scheduled_amount || 0)}</TableCell>
                      <TableCell>
                        {paymentPoDetails?.vendor_name ||
                          vendorBillingData?.[0]?.vendor_name ||
                          'No Vendor Name'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Auto-paid on creation tag and helper text */}
            <Box sx={{ mb: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip
                  label="Auto-paid on creation"
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: '#e3f2fd',
                    borderColor: '#1976d2',
                    color: '#1565c0'
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.875rem',
                  fontStyle: 'italic',
                  lineHeight: 1.4
                }}>
                Scheduled bills will automatically sync to QuickBooks immediately upon creation and
                will be marked as paid in the system.
              </Typography>
            </Box>

            <Typography component="div" sx={{ marginBottom: '30px !important' }}>
              Create payment schedule by adding the following fields.
            </Typography>
            {data?.map((d, i) => (
              <Box
                key={i}
                component="form"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  border: `1px solid #dadada`,
                  borderRadius: 1,
                  p: 2,
                  mb: 3
                }}>
                <fieldset style={{ borderColor: theme.palette.divider }}>
                  <StyledLegend>
                    Payment Schedule
                    <div
                      onClick={() => handleDeletePaymentSchedule(Number(d.index))}
                      style={{
                        cursor: 'pointer',
                        display: 'inline-block',
                        margin: '0 0 6px 12px'
                      }}>
                      <img
                        className="img-fluid link-icon"
                        src={DeleteIcon}
                        alt="Delete Icon"
                        // style={{
                        //   pointerEvents: d.disabled ? 'none' : 'auto'
                        // }}
                      />
                    </div>
                  </StyledLegend>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextfield
                        type="number"
                        min="0"
                        labelText="Amount"
                        name={`data.${i}.amount`}
                        value={d.amount?.toString() ?? ''}
                        onChange={(event) => handleChangePaymentSchedule(event, i, 'amount')}
                        isRequired
                        maxLength={250}
                        validator={validator}
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextfield
                        type="number"
                        labelText="PO Total in %"
                        name={`data.${i}.po_in_total`}
                        value={d.po_in_total}
                        onChange={(event) => handleChangePaymentSchedule(event, i, 'po_in_total')}
                        isRequired
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextfield
                        validator={validator}
                        labelText="Purchase Date"
                        name={`data.${i}.due_date`}
                        value={d.due_date}
                        isRequired={true}
                        onChange={(event) => handleChangePaymentSchedule(event, i, 'due_date')}
                        type="date"
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DropDown
                        label="Payment Method"
                        inputName={`data.${i}.payment_method`}
                        optionText="Select payment method"
                        value={d.payment_method || ''}
                        onChange={(event) =>
                          handleChangePaymentSchedule(event, i, 'payment_method')
                        }
                        options={paymentMethods}
                        validator={validator}
                        required
                      />
                    </Grid>
                  </Grid>
                </fieldset>
              </Box>
            ))}
            <Typography
              component="div"
              sx={{ width: '100%', textAlign: 'center', marginTop: '30px !important' }}>
              <CustomButton
                onClick={handleAddPaymentSchedule}
                type="info"
                label={
                  <>
                    <img src={AddCirlce} style={{ marginRight: '10px' }} /> Add another payment
                  </>
                }
              />
            </Typography>
          </>
        }
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {onBackToCreatePo && (
              <CustomButton
                onClick={onBackToCreatePo}
                variant="outlined"
                label="Back to PO"
                disabled={loader}
              />
            )}
            <Box sx={{ marginLeft: 'auto' }}>
              <CustomButton
                onClick={onClose}
                variant="outlined"
                label="Discard"
                disabled={loader}
              />
              {/* <CustomButton
                onClick={() => {
                  setDelayPaymentScheduleModal(true);
                  setShowModal(false);
                }}
                label="Delay Payment Schedule Creation"
                disabled={loader}
              /> */}
              <CustomButton
                disabled={!data?.length || loader}
                onClick={submitForm}
                label="Create Payment Schedule"
              />
            </Box>
          </Box>
        }
      />

      <CustomDialog
        title={
          <Typography component="div" sx={{ color: '#3cbe8e' }}>
            <img src={CheckCirleSuccess} />
            Delay Payment Schedule Creation
          </Typography>
        }
        open={delayPaymentScheduleModal}
        maxWidth="xs"
        closeDialog={() => {
          setShowModal(true);
          setDelayPaymentScheduleModal(false);
        }}
        loading={loader}
        content={
          <Typography sx={{ mb: 2 }}>
            You have chosen to delay the creation of a payment schedule. A bill for 100% of the
            amount will be created, the due date will be set to 01-01-2099, and the status will be
            set to &apos;Needs Allocation&apos;. You will need to allocate payments at a later time.
          </Typography>
        }
        actions={
          <>
            <CustomButton
              onClick={() => {
                setShowModal(true);
                setDelayPaymentScheduleModal(false);
              }}
              label="Go Back"
              disabled={loader}
            />
            <CustomButton
              onClick={() => {
                handleDelayPaymentScheduleCreation();
              }}
              label="Proceed"
              disabled={loader}
            />
          </>
        }
      />
    </>
  );
}
